import createApolloClient from "./graphql/ApolloClient";
import { GET_ALL_PRODUCTS } from "@/graphql/queries";

const client = createApolloClient();

/**
 * Fetches the latest product data from the server for real-time stock checks.
 * Throws if the product no longer exists.
 */
const fetchLatestProduct = async (productId) => {
  const { data } = await client.query({
    query: GET_ALL_PRODUCTS,
    variables: {
      where: { id: { _eq: productId } },
      limit: 1,
    },
    fetchPolicy: "network-only",
  });

  const latestProduct = data?.product_products?.[0];
  if (!latestProduct) throw new Error("Product no longer available");
  return latestProduct;
};

/**
 * Calculates how much of a specific variant size is already in the cart.
 */
const getExistingQtyInCart = (items, productId, productVariationId, variantSizeId, metal) => {
  let total = 0;
  items.forEach((item) => {
    if (
      item.product_id === productId &&
      (item.product_variation_id ?? null) === (productVariationId ?? null)
    ) {
      (item.cart_item_sizes || []).forEach((size) => {
        if (size.is_deleted) return;
        if (size.variant_size_id === variantSizeId && size.metal === (metal || "")) {
          total += size.quantity || 0;
        }
      });
    }
  });
  return total;
};

/**
 * Real-time stock check for wholesale (multi-size) selections.
 */
export const verifyWholesaleStock = async (product, items) => {
  const latestProduct = await fetchLatestProduct(product.product_id);

  for (const selection of product.wholesaleSelections.filter((s) => s.qty > 0)) {
    const latestVariant = latestProduct.product_variations?.find(
      (v) => v.id === product.product_variation_id
    );
    if (!latestVariant) throw new Error("Variant no longer available");

    const latestSize = latestVariant.product_variant_sizes?.find(
      (s) => s.id === selection.variantSizeId
    );
    if (!latestSize) {
      throw new Error(`Size ${selection.size_value || selection.variantSizeId} no longer available`);
    }

    const existingQty = getExistingQtyInCart(
      items,
      product.product_id,
      product.product_variation_id,
      selection.variantSizeId,
      selection.metal
    );

    const latestStock = latestSize.quantity || 0;
    const newTotal = existingQty + selection.qty;

    console.log(`🔍 Real-time check for size ${selection.variantSizeId}:
  - Latest DB stock: ${latestStock}
  - Existing in cart: ${existingQty}
  - Trying to add: ${selection.qty}
  - New total would be: ${newTotal}`);

    if (newTotal > latestStock) {
      const available = Math.max(0, latestStock - existingQty);
      throw new Error(
        available <= 0
          ? `Product is now out of stock. Stock has changed, please refresh and try again.`
          : `Only ${available} available now (stock has changed). You have ${existingQty} in cart. Please refresh and try again.`
      );
    }
  }
};

/**
 * Real-time stock check for single-size products.
 */
export const verifySingleSizeStock = async (product, items) => {
  const latestProduct = await fetchLatestProduct(product.product_id);

  const latestVariant = latestProduct.product_variations?.find(
    (v) => v.id === product.product_variation_id
  );
  if (!latestVariant) throw new Error("Variant no longer available");

  const latestSize = latestVariant.product_variant_sizes?.find(
    (s) => s.id === product.variant_size_id
  );
  if (!latestSize) throw new Error("Size no longer available");

  const existingQty = getExistingQtyInCart(
    items,
    product.product_id,
    product.product_variation_id,
    product.variant_size_id,
    product.metal
  );

  const latestStock = latestSize.quantity || 0;
  const newTotal = existingQty + product.quantity;

  console.log(`🔍 Real-time check:
  - Latest DB stock: ${latestStock}
  - Existing in cart: ${existingQty}
  - Trying to add: ${product.quantity}
  - New total would be: ${newTotal}`);

  if (newTotal > latestStock) {
    const available = Math.max(0, latestStock - existingQty);
    throw new Error(
      available <= 0
        ? `Item is now out of stock. Stock has changed, please refresh and try again.`
        : `Only ${available} available now (stock has changed). You have ${existingQty} in cart. Please refresh and try again.`
    );
  }
};

/**
 * Real-time stock check for simple products (no sizes).
 */
export const verifySimpleProductStock = async (product, items) => {
  const latestProduct = await fetchLatestProduct(product.product_id);

  const stockToCheck = latestProduct.available_quantity ?? latestProduct.quantity;
  if (stockToCheck === undefined || stockToCheck === null) return;

  let existingQty = 0;
  items.forEach((item) => {
    if (
      item.product_id === product.product_id &&
      (item.product_variation_id ?? null) === (product.product_variation_id ?? null) &&
      !item.is_deleted
    ) {
      existingQty += item.quantity || 0;
    }
  });

  const newTotal = existingQty + product.quantity;

  console.log(`🔍 Real-time check:
  - Latest DB stock: ${stockToCheck}
  - Existing in cart: ${existingQty}
  - Trying to add: ${product.quantity}
  - New total would be: ${newTotal}`);

  if (newTotal > stockToCheck) {
    const available = Math.max(0, stockToCheck - existingQty);
    throw new Error(
      available <= 0
        ? `Item is now out of stock. Stock has changed, please refresh and try again.`
        : `Only ${available} available now (stock has changed). You have ${existingQty} in cart. Please refresh and try again.`
    );
  }
};

/**
 * Entry point — runs the appropriate real-time stock check based on product type.
 */
export const verifyRealtimeStock = async (product, items) => {
  const isWholesale = (product.sub_category_name ?? '').toLowerCase().includes('wholesale');

  if (isWholesale) {
    console.log('⚡ Wholesale product — skipping real-time stock verification');
    return;
  }

  const hasWholesaleSelections =
    Array.isArray(product.wholesaleSelections) && product.wholesaleSelections.length > 0;

  console.log("🔄 Verifying real-time stock availability...");

  if (hasWholesaleSelections) {
    await verifyWholesaleStock(product, items);
  } else if (product.variant_size_id) {
    await verifySingleSizeStock(product, items);
  } else {
    await verifySimpleProductStock(product, items);
  }

  console.log("✅ REAL-TIME STOCK VERIFICATION PASSED");
};